import { Dropdown, DropdownItem, Spinner } from '@frontend/components/common';
import { DetailModalHeader, ModalTabNav } from '@frontend/components/modal';
import { CommonMessage } from '@frontend/lang';
import React from 'react';
import { LuEllipsisVertical } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { PlatformDetailEntry } from '../platform-modal-host/platform-detail-url';
import { getReactiveDetailRegistryEntry } from './reactive-detail-registry';
import { useReactiveDetailShell } from './reactive-detail-shell.controller';
import { AnyReactiveDetailRegistryEntry } from './reactive-detail.types';

export interface ReactiveDetailShellProps {
    detail: PlatformDetailEntry;
}

/** Detail pane for one `platform_details` entry, rendered from the reactive detail registry. */
export const ReactiveDetailShell = ({ detail }: ReactiveDetailShellProps) => {
    const registryEntry = getReactiveDetailRegistryEntry(detail.type);
    if (!registryEntry) {
        return (
            <ShellMessage testId='reactive-detail-shell-unknown'>
                <FormattedMessage
                    id='platform.modal.reactive-detail-shell.reactive-detail-shell.unsupported'
                    description='Shown in a detail pane when the opened entity type has no platform detail view yet'
                    defaultMessage='These details cannot be shown here yet.'
                />
            </ShellMessage>
        );
    }
    return (
        <ReactiveDetailPane
            detail={detail}
            registryEntry={registryEntry}
        />
    );
};

const ReactiveDetailPane = ({ detail, registryEntry }: { detail: PlatformDetailEntry; registryEntry: AnyReactiveDetailRegistryEntry }) => {
    const { status, view, tabs, activeTab, activeDropdownOption, onChangeTab, onChangeDropdown } = useReactiveDetailShell(detail, registryEntry);

    if (status === 'loading') {
        return (
            <div
                className='modal bg-background-default flex h-full items-center justify-center p-6'
                data-testid='reactive-detail-shell-loading'>
                <Spinner className='text-border-default fill-brand-primary size-14' />
            </div>
        );
    }
    if (status === 'not-found' || !view) {
        return <ShellMessage testId='reactive-detail-shell-not-found'>{CommonMessage.ERRORS.NOT_FOUND}</ShellMessage>;
    }
    if (status === 'error') {
        return (
            <ShellMessage testId='reactive-detail-shell-error'>
                <FormattedMessage
                    id='platform.modal.reactive-detail-shell.reactive-detail-shell.load-error'
                    description='Shown in a detail pane when the additional details of an entity could not be loaded'
                    defaultMessage='These details could not be loaded.'
                />
            </ShellMessage>
        );
    }

    const headerActions = registryEntry.headerActions?.(view);
    const compactItems = registryEntry.compactHeaderActionItems?.(view) ?? [];

    return (
        <div
            className='modal bg-background-default flex h-full flex-col gap-6 p-6'
            data-testid='reactive-detail-shell'
            data-entity-type={detail.type}
            data-entity-id={detail.id}>
            <div className='modal-header flex flex-col gap-3 px-0 py-0 sm:flex-row sm:items-start sm:justify-between'>
                <DetailModalHeader title={registryEntry.title(view)} />
                {(headerActions || compactItems.length > 0) && (
                    <div className='flex w-full items-center justify-end gap-2 sm:w-auto'>
                        <div className={compactItems.length > 0 ? 'hidden items-center gap-2 lg:flex' : 'flex items-center gap-2'}>{headerActions}</div>
                        {compactItems.length > 0 && (
                            <div className='lg:hidden'>
                                <Dropdown
                                    label={<LuEllipsisVertical className='text-brand-muted text-lg font-bold' />}
                                    buttonStyle='size-11 hover:bg-brand-light cursor-pointer bg-background-surface rounded-md border border-border-default flex justify-center items-center'
                                    hideChevron>
                                    {compactItems.map((item, index) => (
                                        <DropdownItem
                                            key={index}
                                            {...item}
                                        />
                                    ))}
                                </Dropdown>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {tabs.length > 1 && (
                <ModalTabNav
                    modalTabItems={tabs.map((tab) => ({
                        identifier: tab.id,
                        label: tab.label,
                        dropdownOptions:
                            'dropdownOptions' in tab
                                ? tab.dropdownOptions.map((option) => ({ identifier: option.id, label: option.label, children: null }))
                                : undefined
                    }))}
                    activeSubsection={activeTab?.id}
                    activeDropdownOption={activeDropdownOption?.id}
                    onChange={onChangeTab}
                    onDropdownChange={onChangeDropdown}
                />
            )}
            <div className='no-scrollbar flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto'>
                {activeDropdownOption ? activeDropdownOption.render(view) : activeTab && 'render' in activeTab ? activeTab.render(view) : null}
            </div>
        </div>
    );
};

const ShellMessage = (props: { testId: string; children: React.ReactNode }) => (
    <div
        className='modal bg-background-default flex h-full items-center justify-center p-6'
        data-testid={props.testId}>
        <p className='text-brand-muted text-base font-medium'>{props.children}</p>
    </div>
);
